import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { ErrorContext } from "../../../../components/layout/error-snackbar"
import { Children } from "../../../../components/miscellianous/children"
import { RseeDocument } from "../../../dto/rsee/rsee-document"
import { useRsee } from "../../../hooks/rsee/use-rsee"
import { OrganizationContext } from "../../organization/organization-context"
import { ProjectContext } from "../../project/ProjectContext"
import { ProjectListContext } from "../../project/ProjectListContext"

export const RseeDocumentListContext = React.createContext<RseeDocumentListStore>({} as RseeDocumentListStore)

export function RseeDocumentListContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const openErrorSnackbar = useContext(ErrorContext)
  const { fetchRseeDocumentList, addRseeDocument } = useRsee()

  const { refreshOrganization } = useContext(OrganizationContext)
  const { refreshAllProjectCard } = useContext(ProjectListContext)
  const { project } = useContext(ProjectContext)

  const [rseeDocumentList, setRseeDocumentList] = useState<RseeDocument[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const refreshRseeDocumentList: () => Promise<RseeDocument[]> = useCallback(() => {
    if (project?.id) {
      setIsLoading(true)
      return fetchRseeDocumentList(project?.id)
        .then((newBSProjectList) => {
          setRseeDocumentList(newBSProjectList)
          return newBSProjectList
        })
        .finally(() => setIsLoading(false))
    }
    return Promise.resolve([])
  }, [fetchRseeDocumentList, project?.id])

  const addDocument: (rseeFile: File) => Promise<string | undefined> = useCallback(
    (rseeFile) => {
      if (project?.id) {
        return addRseeDocument(rseeFile, project.id).then((newDocumentId) => {
          refreshOrganization()
          refreshAllProjectCard()
          refreshRseeDocumentList()
          return newDocumentId
        })
      }
      openErrorSnackbar(new Error("Erreur: il n'y a pas de projet Beem Pilot sélectionné"))
      return Promise.resolve(undefined)
    },
    [addRseeDocument, refreshAllProjectCard, refreshRseeDocumentList, openErrorSnackbar, project?.id, refreshOrganization]
  )

  useEffect(() => {
    refreshRseeDocumentList()
  }, [refreshRseeDocumentList])

  const rseeProjectListStore = useMemo(
    () => ({
      rseeDocumentList,
      setRseeDocumentList,
      isLoading,
      setIsLoading,
      addDocument,
      refreshRseeDocumentList,
    }),
    [isLoading, rseeDocumentList, addDocument, refreshRseeDocumentList]
  )
  return <RseeDocumentListContext.Provider value={rseeProjectListStore}>{children}</RseeDocumentListContext.Provider>
}

export interface RseeDocumentListStore {
  rseeDocumentList: RseeDocument[]
  setRseeDocumentList: Dispatch<SetStateAction<RseeDocument[]>>
  isLoading: boolean
  setIsLoading: Dispatch<SetStateAction<boolean>>

  addDocument(rseeFile: File): Promise<string | undefined>
  refreshRseeDocumentList(): Promise<RseeDocument[]>
}
